<template>
    <div class="tab-box">
        <!-- 左侧切换按钮 -->

        <!-- 标签容器 -->
        <div class="tab-container" ref="containerRef">
            <div class="tab-wrapper" ref="wrapperRef">
                <div
                    v-for="option in options"
                    :key="option.value"
                    class="tab-item"
                    :class="{ 'tab-item--active': modelValue === option.value }"
                    @click="handleSelect(option.value)"
                >
                    {{ option.label }}
                </div>
            </div>
        </div>

        <div class="tab-action" v-if="showActions">
            <i
                @click="scrollLeftHandler"
                :disabled="!canScrollLeft"
                class="iconfont icon-arrow_08_left"
                :class="{ 'disabled': !canScrollLeft }"
            ></i>
            <i
                @click="scrollRightHandler"
                :disabled="!canScrollRight"
                class="iconfont icon-arrow_08_right"
                :class="{ 'disabled': !canScrollRight }"
            ></i>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'

// 组件属性定义
const props = defineProps({
    // 当前选中值，支持v-model双向绑定
    modelValue: {
        type: [String, Number],
        default: ''
    },
    // 选项数组，每项包含label和value字段
    options: {
        type: Array,
        default: () => [],
        validator: (value) => {
            return value.every(item => 
                typeof item === 'object' && 
        'label' in item && 
        'value' in item)
        }
    },
    // 每次滚动的距离（像素）
    scrollStep: {
        type: Number,
        default: 200
    }
})

// 事件定义
const emit = defineEmits(['update:modelValue', 'change'])

// 响应式引用
const containerRef = ref(null)
const wrapperRef = ref(null)
const scrollLeft = ref(0)
const maxScrollLeft = ref(0)

// 计算属性：是否可以向左滚动
const canScrollLeft = computed(() => scrollLeft.value > 0)

// 计算属性：是否可以向右滚动
const canScrollRight = computed(() => scrollLeft.value < maxScrollLeft.value)

// 计算属性：是否需要展示切换按钮（内容是否超出一屏）
const showActions = computed(() => maxScrollLeft.value > 0)

// 处理选项选择
const handleSelect = (value) => {
    emit('update:modelValue', value)
    emit('change', value)
}

// 向左滚动
const scrollLeftHandler = () => {
    if (!canScrollLeft.value) {
        return 
    }
  
    const newScrollLeft = Math.max(0, scrollLeft.value - props.scrollStep)
    scrollToPosition(newScrollLeft)
}

// 向右滚动
const scrollRightHandler = () => {
    if (!canScrollRight.value) {
        return 
    }
  
    const newScrollLeft = Math.min(maxScrollLeft.value, scrollLeft.value + props.scrollStep)
    scrollToPosition(newScrollLeft)
}

// 滚动到指定位置
const scrollToPosition = (position) => {
    if (containerRef.value) {
        containerRef.value.scrollTo({
            left: position,
            behavior: 'smooth'
        })
    }
}

// 更新滚动状态
const updateScrollState = () => {
    if (containerRef.value && wrapperRef.value) {
        scrollLeft.value = containerRef.value.scrollLeft
        maxScrollLeft.value = Math.max(0, wrapperRef.value.scrollWidth - containerRef.value.clientWidth)
    }
}

// 处理容器滚动事件
const handleScroll = () => {
    updateScrollState()
}

// 处理窗口大小变化
const handleResize = () => {
    nextTick(() => {
        updateScrollState()
    })
}

// 滚动到选中项
const scrollToActiveItem = () => {
    if (!containerRef.value || !wrapperRef.value) {
        return 
    }
  
    const activeItem = wrapperRef.value.querySelector('.tab-item--active')
    if (!activeItem) {
        return 
    }
  
    const containerRect = containerRef.value.getBoundingClientRect()
    const itemRect = activeItem.getBoundingClientRect()
  
    // 如果选中项不在可视区域内，滚动到该项
    if (itemRect.left < containerRect.left || itemRect.right > containerRect.right) {
        const itemOffsetLeft = activeItem.offsetLeft
        const itemWidth = activeItem.offsetWidth
        const containerWidth = containerRef.value.clientWidth
    
        let targetScrollLeft = itemOffsetLeft - (containerWidth - itemWidth) / 2
        targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft.value))
    
        scrollToPosition(targetScrollLeft)
    }
}

// 监听modelValue变化，自动滚动到选中项
watch(() => props.modelValue, () => {
    nextTick(() => {
        scrollToActiveItem()
    })
})

// 监听options变化，更新滚动状态
watch(() => props.options, () => {
    nextTick(() => {
        updateScrollState()
        scrollToActiveItem()
    })
}, { deep: true })

// 组件挂载后初始化
onMounted(() => {
    nextTick(() => {
        updateScrollState()
        scrollToActiveItem()
    })
  
    // 添加事件监听器
    if (containerRef.value) {
        containerRef.value.addEventListener('scroll', handleScroll)
    }
    window.addEventListener('resize', handleResize)
})

// 组件卸载前清理
onUnmounted(() => {
    if (containerRef.value) {
        containerRef.value.removeEventListener('scroll', handleScroll)
    }
    window.removeEventListener('resize', handleResize)
})

// 暴露方法供外部调用
defineExpose({
    scrollLeft: scrollLeftHandler,
    scrollRight: scrollRightHandler,
    scrollToActiveItem
})
</script>

<style lang="scss">
@import '@/theme/media.scss';
@import '@/theme/mixin.scss';

.tab-box {
  display: flex;
  gap: 10px;
  
  .tab-container {
    flex: 1;
    overflow: hidden;
    position: relative;
    
    .tab-wrapper {
      display: flex;
      gap: 30px;
      transition: transform 0.3s ease;
      
      .tab-item {
        display: flex;
        align-items: center;
        color: var(--g-primary-color);
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
        font-size: 18px;
        line-height: 27px;
        padding-bottom: 15px;
        border-bottom: 3px solid transparent;
        user-select: none;
        flex-shrink: 0;
        opacity: 0.5;
        
        &:hover {
          opacity: 1;
        }
        
        &.tab-item--active {
          border-color: var(--g-primary-color);
          font-weight: 500;
          opacity: 1;
        }
      }
    }
  }

  .tab-action {
    width: 50px;
    height: 30px;
    box-sizing: border-box;
    border: 1px solid var(--color-border);
    border-radius: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    position: relative;
    
    &::before {
        content: '';
        position: absolute;
        left: -30px;
        top: 0;
        width: 20px;
        height: 100%;
        background: linear-gradient(270deg, var(--g-bg-color) 0%, rgba(255, 255, 255, 0) 100%);
    }

    .iconfont {
      font-size: 12px;
      cursor: pointer;
      color: var(--g-primary-color);
      &.disabled {
        color: var(--g-tip-color);
        cursor: not-allowed;
      }
    }
  }
  
  @include media-800 {
    .tab-container {
        overflow-x: auto;
        &::-webkit-scrollbar {
            display: none;
        }
        .tab-wrapper {
            gap: 20px;
            
            .tab-item {
            font-size: 14px;
            line-height: 21px;
            // padding-bottom: 10px;
            }
        }
    }
    .tab-action {
        display: none;
    }
  }
}
</style>
